import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { Password } from 'primereact/password'
import { useId } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import useToast from '~/hooks/toast'
import redaxiosInstance, { type ErrorObj } from '~/utils/httpClient'

export const loginBodySchema = z.object({
  email: z.email(),
  password: z
    .string('password is required')
    .min(8, 'password must be minimum of 8 characters')
    .regex(/[a-z]/, 'password must contain at least 1 lowercase letters')
    .regex(/[A-Z]/, 'password must contain at least 1 uppercase letters')
    .regex(/[0-9]/, 'password must contain at least 1 number'),
  remember: z.boolean().nullable(),
})

const LoginPage = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const emailId = useId()
  const passwordId = useId()
  const rememberId = useId()

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<z.infer<typeof loginBodySchema>>({
    resolver: zodResolver(loginBodySchema),
    defaultValues: {
      email: '',
      password: '',
      remember: false,
    },
  })
  watch()

  const onSubmit = (data: z.infer<typeof loginBodySchema>) => {
    redaxiosInstance
      .post('/api/auth/login', data)
      .then(() => {
        toast?.current?.show({
          severity: 'success',
          summary: 'Login Successful',
          life: 2000,
        })
        navigate('/controls')
        reset()
      })
      .catch((e: ErrorObj) => {
        toast?.current?.show({
          severity: 'error',
          summary: 'Error logging you in',
          detail: e.message,
        })
      })
  }

  return (
    <div className='flex flex-col justify-center items-center min-h-screen w-full bg-teal-200'>
      <div className='w-60 md:w-80 p-4 py-6 rounded-md rounded-md shadow-md bg-white'>
        <h1 className='font-bold text-center text-2xl mb-6'>ISO Management</h1>
        <form onSubmit={handleSubmit(onSubmit)} className='p-fluid'>
          <div className='field my-4'>
            <span className='p-float-label'>
              <InputText
                id={emailId}
                {...control.register('email')}
                className={errors.email ? 'p-invalid' : ''}
              />
              <label htmlFor={emailId}>Email</label>
            </span>
            {errors.email && (
              <Message severity='error' text={errors.email.message} className='mt-2' />
            )}
          </div>

          <div className='field my-4'>
            <span className='p-float-label'>
              <Password
                id={passwordId}
                {...control.register('password')}
                onChange={(e) => setValue('password', e.target.value)}
                toggleMask
                className={errors.password ? 'p-invalid' : ''}
                feedback={false}
              />
              <label htmlFor={passwordId}>Password</label>
            </span>
            {errors.password && (
              <Message severity='error' text={errors.password.message} className='mt-2' />
            )}
          </div>

          <div className='field-checkbox my-4'>
            <Checkbox
              inputId={rememberId}
              onChange={(c) => {
                setValue('remember', c.checked || false)
              }}
              checked={getValues('remember') || false}
            />
            <label htmlFor={rememberId} className='ml-2'>
              Remember my login
            </label>
          </div>
          <Button type='submit' label='Login' className='mt-4' />
          <div className='text-center mt-4'>
            <a
              href='/auth/forgot-password'
              className='text-teal-600 hover:underline cursor-pointer'
              onClick={(e) => {
                e.preventDefault()
                navigate('/auth/forgot-password')
              }}
            >
              Forgot Password?
            </a>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
